// Copyright 2025-2026 - Hewlett Packard Enterprise Company

import { useContext, useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { ContextualHelp as LegacyContextualHelp } from '@glcp/contextual-help/dist/ContextualHelp';
import ShellContextualHelpShim from './ShellContextualHelpShim';

import useFlags from '../../hooks/useFlags';
import { SidebarContext } from '../../providers/SidebarProvider';
import { getLocale } from '../../utils/i18nHelpers';

import ROUTE_CONFIG, { getRouteConfig } from '../../routes/routeConfig';

// Overrides the contextual-help package's hardcoded `font-size: small` heading
// styling. See helpContent.css for details.
import './helpContent.css';

const DEFAULT_TOPIC = 'com-404-tt';

const resolveTopic = (matchedPath, flags) => {
  const pathConfig = getRouteConfig(matchedPath, flags);

  if (pathConfig) {
    const { complexLDFlag, helpTopic } = pathConfig;

    if (!complexLDFlag) {
      return helpTopic || DEFAULT_TOPIC;
    }

    /*
     *
     * handle the complexLDFlag scenario topic lookup here
     * complex LD flags are any scenario besides ||'ing all the flags together
     *
     * example:
     * if (matchedPath === '/my/path' && (flags.MY_FLAG() && !flags.MY_OTHER_FLAG())) {
     *   return helpTopic;
     * }
     *
     */
    if (matchedPath.includes('/analytics/carbonFootprintDetail')) {
      return helpTopic;
    }

    return DEFAULT_TOPIC;
  }

  return DEFAULT_TOPIC;
};

const generateRouteJson = (path, { isExact }) => ({
  path,
  exact: !!isExact,
});

export default function Help({ ...rest }) {
  const flags = useFlags();
  const showTopicUrlFlag =
    flags.T_20250315_18961_CONTEXTUAL_HELP_SHOW_TOPIC_URL_BOOL();
  const useShellContextualHelp =
    flags.T_20260807_70791_USE_NEW_SHELL_CONTEXTUAL_HELP_BOOL();
  const tcaasLocale = getLocale();

  const { pathname } = useLocation();
  const match = Object.keys(ROUTE_CONFIG).find(path =>
    matchPath(pathname, generateRouteJson(path, ROUTE_CONFIG[path])),
  );
  const topic = resolveTopic(match, flags);

  const { setIsContextualHelpOpen } = useContext(SidebarContext);

  const onOpen = useCallback(() => {
    setIsContextualHelpOpen(true);
  }, [setIsContextualHelpOpen]);

  const onClose = useCallback(() => {
    setIsContextualHelpOpen(false);
  }, [setIsContextualHelpOpen]);

  const sharedProps = {
    locale: tcaasLocale,
    topicsId: topic,
    proxy: window.indigo.REACT_APP_HELP_PROXY,
    viewTopicPrefix: showTopicUrlFlag ? 'hpesc_' : null,
    viewAllUrl: 'https://www.hpe.com/info/com-quicklinks',
    onClose,
    hideTopicUrl: !showTopicUrlFlag,
    showSingleTopicList: true,
    ...rest,
  };

  if (useShellContextualHelp) {
    return <ShellContextualHelpShim {...sharedProps} onOpen={onOpen} />;
  }

  return (
    <div className="legacy-contextual-help">
      <LegacyContextualHelp {...sharedProps} onOpen={onOpen} />
    </div>
  );
}
